import { Clipboard } from '@angular/cdk/clipboard';
import { Component, OnDestroy, OnInit } from '@angular/core';
import * as moment from 'moment-timezone';
import { Subject, debounceTime } from 'rxjs';
import Swal from 'sweetalert2';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import {
  BinanceCentralization,
  BinanceCentralizationListFilters,
  CentralizationDashboardResponse,
  CentralizationStatus,
} from '../../interfaces/binance-centralization.interface';
import { BinanceCentralizationService } from '../../services/binance-centralization.service';

@Component({
  selector: 'app-binance-centralizations',
  templateUrl: './binance-centralizations.component.html',
  styleUrls: ['./binance-centralizations.component.sass'],
})
export class BinanceCentralizationsComponent implements OnInit, OnDestroy {
  dashboard: CentralizationDashboardResponse | null = null;
  rows: BinanceCentralization[] = [];

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalItems = 0;
  totalPages = 0;

  // Filters
  filterStatus: CentralizationStatus | '' = '';
  filterQ = '';
  filterDateFrom: string = '';
  filterDateTo: string = '';

  statusOptions: CentralizationStatus[] = [
    'PENDING',
    'TRANSFERRED',
    'COMPLETED',
    'PARTIAL',
    'FAILED',
  ];

  private readonly POLLING_MS = 30_000;
  private pollingTimer: any = null;
  private visibilityHandler = () => {
    if (document.hidden) this.stopPolling();
    else this.startPolling();
  };

  private q$ = new Subject<string>();

  // Mobile accordion
  expandedRowId: string | null = null;

  constructor(
    private centralizationService: BinanceCentralizationService,
    private spinner: SpinnerService,
    private clipboard: Clipboard,
  ) {}

  async ngOnInit(): Promise<void> {
    // Debounce 300ms para la búsqueda libre.
    this.q$.pipe(debounceTime(300)).subscribe(() => {
      this.currentPage = 1;
      this.loadList();
    });

    await Promise.all([this.loadDashboard(), this.loadList()]);
    this.startPolling();
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  ngOnDestroy(): void {
    this.stopPolling();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.q$.complete();
  }

  // ============================================================
  // Data loading
  // ============================================================
  async loadDashboard(silent: boolean = false): Promise<void> {
    try {
      this.dashboard = await this.centralizationService.getDashboard();
    } catch (error) {
      if (!silent) {
        console.warn('[Centralizations] Error cargando dashboard', error);
      }
    }
  }

  async loadList(): Promise<void> {
    const ref = this.spinner.start();
    try {
      const filters: BinanceCentralizationListFilters = {
        page: this.currentPage,
        limit: this.pageSize,
      };
      if (this.filterStatus) filters.status = this.filterStatus;
      if (this.filterQ.trim()) filters.q = this.filterQ.trim();
      if (this.filterDateFrom)
        filters.dateFrom = new Date(this.filterDateFrom).toISOString();
      if (this.filterDateTo)
        filters.dateTo = new Date(this.filterDateTo).toISOString();

      const response = await this.centralizationService.list(filters);
      this.rows = response.data;
      this.totalItems = response.meta.total;
      this.totalPages = response.meta.totalPages;
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo cargar el listado de centralizaciones.',
        confirmButtonColor: '#1e5a3f',
      });
    } finally {
      this.spinner.stop(ref);
    }
  }

  onPageChange(event: any): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex + 1;
    this.loadList();
  }

  onStatusChange(): void {
    this.currentPage = 1;
    this.loadList();
  }

  onQChange(value: string): void {
    this.filterQ = value;
    this.q$.next(value);
  }

  onDateChange(): void {
    this.currentPage = 1;
    this.loadList();
  }

  clearFilters(): void {
    this.filterStatus = '';
    this.filterQ = '';
    this.filterDateFrom = '';
    this.filterDateTo = '';
    this.currentPage = 1;
    this.loadList();
  }

  /** Click en la card de Alertas → filtra a status=FAILED por default. */
  filterByAlerts(): void {
    this.filterStatus = 'FAILED';
    this.currentPage = 1;
    this.loadList();
  }

  refresh(): void {
    this.loadDashboard();
    this.loadList();
  }

  // ============================================================
  // Polling — solo dashboard, no la tabla
  // ============================================================
  private startPolling(): void {
    if (this.pollingTimer || document.hidden) return;
    this.pollingTimer = setInterval(() => {
      this.loadDashboard(true);
    }, this.POLLING_MS);
  }

  private stopPolling(): void {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  // ============================================================
  // UI helpers
  // ============================================================
  toggleRow(id: string): void {
    this.expandedRowId = this.expandedRowId === id ? null : id;
  }

  formatDateShort(iso: string | null): string {
    if (!iso) return '—';
    const d = moment(new Date(iso)).tz('America/Santiago');
    return d.isValid() ? d.format('DD/MM HH:mm') : '—';
  }

  formatDateLong(iso: string | null): string {
    if (!iso) return '—';
    const d = moment(new Date(iso)).tz('America/Santiago');
    return d.isValid() ? d.format('DD/MM/YYYY HH:mm:ss') : '—';
  }

  formatAmount(amount: string | null, decimals: number = 2): string {
    if (!amount) return '0.00';
    const n = Number(amount);
    if (!isFinite(n)) return amount;
    return n.toLocaleString('es-CL', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  shortTx(txId: string | null): string {
    if (!txId) return '—';
    if (txId.length <= 16) return txId;
    return `${txId.slice(0, 8)}...${txId.slice(-6)}`;
  }

  /** "hace 12 min" / "en 3 min" — diff respecto a ahora. */
  relativeTime(iso: string | null): string {
    if (!iso) return '—';
    const d = moment(new Date(iso));
    if (!d.isValid()) return '—';
    return d.fromNow();
  }

  copy(text: string | null, label: string = 'Copiado'): void {
    if (!text) return;
    this.clipboard.copy(text);
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: label,
      showConfirmButton: false,
      timer: 1800,
      timerProgressBar: true,
    });
  }

  // Colors per status (paleta del brief)
  statusBg(status: CentralizationStatus): string {
    switch (status) {
      case 'COMPLETED':
        return '#16a34a';
      case 'TRANSFERRED':
        return '#2563eb';
      case 'PARTIAL':
        return '#f59e0b';
      case 'FAILED':
        return '#dc2626';
      case 'PENDING':
      default:
        return '#9ca3af';
    }
  }

  statusColor(_status: CentralizationStatus): string {
    return '#ffffff';
  }

  needsAttention(status: CentralizationStatus): boolean {
    return status === 'PARTIAL' || status === 'FAILED';
  }

  // ============================================================
  // Detalle (modal)
  // ============================================================
  async openDetail(row: BinanceCentralization): Promise<void> {
    const ref = this.spinner.start();
    try {
      const detail = await this.centralizationService.getById(row.id);
      this.spinner.stop(ref);
      await Swal.fire({
        title: `Centralización ${detail.id.slice(0, 8)}…`,
        html: this.buildDetailHtml(detail),
        confirmButtonText: 'Cerrar',
        confirmButtonColor: '#1e5a3f',
        width: '640px',
      });
    } catch (error: any) {
      this.spinner.stop(ref);
      const status = error?.status;
      Swal.fire({
        icon: 'error',
        title: status === 404 ? 'No encontrada' : 'Error',
        text:
          status === 404
            ? 'No existe centralización con ese ID.'
            : 'No se pudo cargar el detalle.',
        confirmButtonColor: '#1e5a3f',
      });
    }
  }

  private buildDetailHtml(d: BinanceCentralization): string {
    const sectionTitle = (t: string) =>
      `<p style="margin: 14px 0 6px 0; font-size: 0.72rem; font-weight: 700; color: #1e5a3f; text-transform: uppercase; letter-spacing: 0.4px;">${t}</p>`;
    const row = (label: string, value: string) =>
      `<div style="display: flex; justify-content: space-between; gap: 12px; padding: 4px 0; font-size: 0.82rem; border-bottom: 1px dashed #f3f4f6;"><span style="color: #6b7280;">${label}</span><span style="color: #374151; font-weight: 600; word-break: break-all; text-align: right;">${value}</span></div>`;

    const statusBadge = `<span style="display: inline-block; padding: 4px 12px; border-radius: 999px; background: ${this.statusBg(
      d.status,
    )}; color: white; font-size: 0.78rem; font-weight: 700;">${d.status}</span>`;

    let html = `<div style="text-align: left; color: #374151;">
      <div style="margin-bottom: 6px;">${statusBadge}</div>
      ${sectionTitle('Usuario')}
      ${row('Email', d.userEmail || '—')}
      ${row('Nombre', d.userFullName || '—')}
      ${row('Usuario ID', String(d.usuarioId))}

      ${sectionTitle('Depósito on-chain')}
      ${row('TxId', d.txId || '—')}
      ${row('Red / Coin', `${d.network} / ${d.coin}`)}
      ${row('Monto', `${this.formatAmount(d.amount)} ${d.coin}`)}
      ${row('Detectado', this.formatDateLong(d.depositAt))}
      ${row('Registrado', this.formatDateLong(d.recordedAt))}

      ${sectionTitle('Transferencia Binance')}
      ${row('Sub origen', d.subAccountId || '—')}
      ${row('Sub destino (master)', d.masterSubAccountId || '—')}
      ${row('Client TranId', d.clientTranId || '—')}
      ${row('Binance TxnId', d.binanceTransferId || '—')}
      ${row('Transferida', this.formatDateLong(d.transferredAt))}

      ${sectionTitle('Crédito al usuario')}
      ${row('Saldo previo', d.previousBalance ? `${this.formatAmount(d.previousBalance)} ${d.coin}` : '—')}
      ${row('Saldo nuevo', d.newBalance ? `${this.formatAmount(d.newBalance)} ${d.coin}` : '—')}
      ${row('Cardex ID', d.cardexId || '—')}
      ${row('Acreditada', this.formatDateLong(d.creditedAt))}`;

    if (d.errorMessage) {
      html += `${sectionTitle('Error')}
        <div style="padding: 8px 10px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; font-size: 0.78rem; color: #991b1b;">${d.errorMessage}</div>`;
    }

    html += '</div>';
    return html;
  }

  /** Búsqueda directa por TxId (botón al lado del input q). */
  async lookupByTxId(): Promise<void> {
    const { value: txId } = await Swal.fire({
      title: 'Buscar por TxId',
      input: 'text',
      inputLabel: 'Hash on-chain exacto',
      inputPlaceholder: '0x...',
      inputAttributes: { autocapitalize: 'off', autocorrect: 'off' },
      inputValidator: (value) =>
        !value || !value.trim() ? 'Debes ingresar un TxId' : null,
      showCancelButton: true,
      confirmButtonText: 'Buscar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1e5a3f',
    });
    if (!txId) return;

    const ref = this.spinner.start();
    try {
      const detail = await this.centralizationService.getByTxId(txId.trim());
      this.spinner.stop(ref);
      await this.openDetail(detail);
    } catch (error: any) {
      this.spinner.stop(ref);
      const status = error?.status;
      Swal.fire({
        icon: status === 404 ? 'info' : 'error',
        title: status === 404 ? 'Sin resultados' : 'Error',
        text:
          status === 404
            ? 'No encontramos ninguna centralización para este TxId. Puede ser que el cron todavía no lo procese (corre cada 5 min).'
            : 'No se pudo realizar la búsqueda.',
        confirmButtonColor: '#1e5a3f',
      });
    }
  }
}
